import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { v4 as uuid } from 'uuid';
import { useCreateLabel } from '@/features/eventMetadata/hooks/useCreateLabel';
import { useDeleteLabel } from '@/features/eventMetadata/hooks/useDeleteLabel';
import { useUpdateLabelSortOrder } from '@/features/eventMetadata/hooks/useUpdateLabelSortOrder';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { LabelType, type ExperienceLabel } from '@/types/gql.generated';
import { Button, Flex, Input, Text } from '@/ui';
import { SidebarSection } from '../../SidebarKit';
import { SortableLabelTag } from './SortableLabelTag';

const VARIANT_STYLES = {
  who: { bg: 'magicPurple.25', borderColor: 'rgba(93,63,246,0.28)' },
  what: { bg: 'white', borderColor: 'rgba(0,0,0,0.12)' },
} as const;

const bySortOrder = (a: ExperienceLabel, b: ExperienceLabel) =>
  a.sortOrder - b.sortOrder;

type Props = {
  labelType: LabelType;
  title: string;
  description: string;
  placeholder: string;
  variant: keyof typeof VARIANT_STYLES;
};

export const LabelTagsSection = ({
  labelType,
  title,
  description,
  placeholder,
  variant,
}: Props) => {
  const { experience } = usePageExperienceContext();
  const { createLabel } = useCreateLabel(labelType);
  const { removeLabel } = useDeleteLabel(labelType);
  const { updateSortOrder } = useUpdateLabelSortOrder(labelType);

  const sourceLabels =
    labelType === LabelType.Who ? experience.whoLabels : experience.labels;

  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');

  // Local order for optimistic drag-and-drop; re-synced when the source changes.
  const [labels, setLabels] = useState(() =>
    [...sourceLabels].sort(bySortOrder)
  );
  useUpdateEffect(() => {
    setLabels([...sourceLabels].sort(bySortOrder));
  }, [sourceLabels]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = labels.findIndex(({ id }) => id === active.id);
    const newIndex = labels.findIndex(({ id }) => id === over.id);
    const previous = labels;
    const reordered = arrayMove(labels, oldIndex, newIndex);
    setLabels(reordered);
    // Roll the optimistic order back if the server rejects the change.
    updateSortOrder(reordered, { onError: () => setLabels(previous) });
  };

  const handleAdd = () => {
    const text = value.trim();
    if (!text) {
      return;
    }
    createLabel({ id: uuid(), text, sortOrder: labels.length });
    setValue('');
    inputRef.current?.focus();
  };

  const { bg, borderColor } = VARIANT_STYLES[variant];

  return (
    <SidebarSection label={title}>
      <Text color="ink.muted" fontSize="ah-status" lineHeight={1.5} mb={3}>
        {description}
      </Text>

      {labels.length > 0 && (
        <DndContext
          collisionDetection={closestCenter}
          sensors={sensors}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={labels} strategy={rectSortingStrategy}>
            <Flex flexWrap="wrap" gap={2} mb={3}>
              {labels.map((label) => (
                <SortableLabelTag
                  bg={bg}
                  borderColor={borderColor}
                  id={label.id}
                  key={label.id}
                  text={label.text}
                  onRemove={removeLabel}
                />
              ))}
            </Flex>
          </SortableContext>
        </DndContext>
      )}

      <Flex align="center" gap={2}>
        <Input
          flex={1}
          fontSize="ah-control"
          placeholder={placeholder}
          ref={inputRef}
          size="sm"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          flexShrink={0}
          size="ah-sm"
          variant="ahPrimary"
          onClick={handleAdd}
        >
          Add
        </Button>
      </Flex>
    </SidebarSection>
  );
};
